import { Component, Input, OnInit } from '@angular/core';
import {
  Price,
  ProductModifier,
  ProductModifierGroup, ProductModifierValidation,
  ProductVariation,
  Restaurant,
  RestaurantProduct, WorkDay
} from '../../core/models/restaurant.model';
import { ItemService } from '../../core/services/item.service';
import { ModifierSelectionType } from '../../core/enums/util.enum';
import { MenuItem, MenuItemData, MenuItemForPayment } from '../../core/models/cart-item.model';
import { CartStore } from '../../core/stores/cart.store';
import { RestaurantStore } from '../../core/stores/restaurant.store';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteService } from '../../core/services/route.service';
import { ItemValidateService } from '../../core/services/item-validate.service';
import { FormatDataService } from '../../core/services/format-data.service';
import { fromEvent } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { URL } from '../../core/enums/url.enum';

@Component({
  selector: 'app-service-item',
  templateUrl: './service-item.component.html',
  styleUrls: ['./service-item.component.scss'],
})
export class ServiceItemComponent implements OnInit {
  public product: RestaurantProduct = new RestaurantProduct();
  public restaurant: Restaurant;
  public variationsInvalidMessage = '';
  public cartProduct: MenuItemForPayment;
  public currencySign = '$';
  public chosenVariation: ProductVariation;
  public modifiersValidations: ProductModifierValidation[] = [];
  public showInvalidMessage = false;
  public locationId: string;
  public hoursOfOperation: WorkDay[] = [];
  public isLoad: boolean = true;

  public readonly truncateDescriptionLength = 3;

  private firstVariationIndex = 0;
  private readonly quantityThreshHold = 1;
  private readonly defaultQuantity = 1;


  constructor(
    private itemService: ItemService,
    private cartStore: CartStore,
    private restaurantStore: RestaurantStore,
    private route: ActivatedRoute,
    private router: Router,
    private routeService: RouteService,
    public formatDataService: FormatDataService,
    private leadTimeService: ItemValidateService

  ) { }


  public get isOnlyOneVariation(): boolean {
    const { variations } = this.product;
    return variations.length === 1;
  }

  public get enlargedImages(): boolean {
    return this.product.info.imageUrl !== null ? this.restaurant?.enlargeProductImages : false;
  }

  public get isUseInventory(): boolean {
    if (!this.chosenVariation) {
      return false;
    }
    return this.chosenVariation.trackInventory && this.chosenVariation.useInventory;
  }

  public get showInvalid(): boolean {
    return this.showInvalidMessage;
  }

  public get isValid(): boolean {
    return !this.modifiersValidations.find((data) => data.isValid === false);
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.locationId = params.locationId;
    });
    this.getServiceItems();
  }

  private getServiceItems(): void {
    this.isLoad = false;
    this.itemService.getServiceItems(this.routeService.getMerchantId(), this.locationId)
      .subscribe((item: RestaurantProduct ) => {
        this.product = item;
        this.initProduct();
        this.isLoad = true;
      }, () => {
        this.isLoad = true;
      });
  }

  private sortVariations() {
    if (this.isUseInventory) {
      this.sortVariationsByInStock();
    }
  }

  private initProduct(): void {
    const filteredVariationList = this.getFilteredVariations(this.product.variations);
    if (filteredVariationList.length === 0) {
      return;
    }
    this.firstVariationIndex = this.product.variations.findIndex((variation: ProductVariation) => variation.id === filteredVariationList[0].id);
    const firstVariationItem = this.product.variations[this.firstVariationIndex];
    this.chosenVariation = this.product.variations[this.firstVariationIndex];
    const productInfo = this.product.info;
    // sort modifier groups
    this.product.modifierGroups.sort((a, b) => a.name.localeCompare(b.name));

    let modifiers: ProductModifier[] = [];
    for (const group of this.product.modifierGroups) {
      const selectedModifiers: ProductModifier[] = group.modifiers.filter(modifier => this.formatDataService.containsKeyword(modifier.name, 'SELECTED') && !this.hiddenWithCost(modifier));
      const copiedSelectedModifiers = [];
      for (const modifier of selectedModifiers) {
        copiedSelectedModifiers.push(this.deepCopyModifiers(modifier));
      }
      if (selectedModifiers.length > 0) {
        modifiers = [...modifiers, ...copiedSelectedModifiers];
      } else if (group.selectionType === ModifierSelectionType.single
        && !this.formatDataService.containsKeyword(group.name, 'HIDDEN') && !this.hiddenWithCost(group.modifiers[0])) {
        group.selectionType = ModifierSelectionType.single;
        modifiers = [...modifiers, this.deepCopyModifiers(group.modifiers[0])];
      }
    }

    const menuItem = new MenuItemData(
      firstVariationItem.id,
      productInfo.name,
      productInfo.imageUrl,
      this.defaultQuantity,
      firstVariationItem,
      modifiers,
      productInfo.description
    );
    this.cartProduct = new MenuItemForPayment(
      this.locationId,
      this.product.categoryId,
      productInfo.id,
      menuItem,
      this.product.isService
    );
    for (const group of this.product.modifierGroups) {
      this.modifiersValidations.push(new ProductModifierValidation(group.id, this.validateItemModifiers(group)));
    }
  }

  public isActiveVariation(variation: ProductVariation): boolean {
    return this.cartProduct && this.cartProduct.item.variation.name === variation.name;
  }

  public isEditingVariation(variation: ProductVariation): boolean {
    return this.cartProduct && this.cartProduct.item.id === variation.id;
  }

  public hiddenWithCost(modifier: ProductModifier): boolean {
    return this.formatDataService.containsKeyword(modifier.name, 'HIDDEN') && modifier.price.amount > 0;
  }

  public getVariationQuantityById(variationId: string): number {
    const productVariation: ProductVariation = this.product.variations.find((variation) => variation.id === variationId);
    return productVariation ? this.getVariationQuantity(productVariation) : Number.MAX_VALUE;
  }

  public getVariationQuantity(variation: ProductVariation): number {
    if (variation.quantityRemaining === null) {
      return Number.MAX_VALUE;
    }
    let cartQuantityToDeduct: number = this.cartStore.getVariationQuantityInCart(variation.id);
    if (this.isEditingVariation(variation)) {
      cartQuantityToDeduct = 0;
    }
    let currentQuantityToDeduct = 1;
    if (this.isActiveVariation(variation)) {
      currentQuantityToDeduct = this.cartProduct.item.quantity;
    }
    return variation.quantityRemaining - currentQuantityToDeduct - cartQuantityToDeduct;
  }

  public isVariationInStock(variation: ProductVariation): boolean {
    return this.getVariationQuantity(variation) >= 0 && !variation.soldOut;
  }

  public isSelectedProductInStock(product: RestaurantProduct): boolean {
    if (!this.isUseInventory && !this.containsSoldOut(product)) {
      return true;
    }
    for (const variation of product.variations) {
      if (this.isActiveVariation(variation) &&
        this.isVariationInStock(variation) &&
        this.currentQuantityRemaining(product) >= 0) {
        return true;
      }
    }
    return false;
  }

  public containsSoldOut(product: RestaurantProduct): boolean {
    return product.variations.filter(variation => variation.soldOut).length > 0;
  }

  public currentQuantityRemaining(product: RestaurantProduct): number {
    for (const variation of product.variations) {
      if (this.isActiveVariation(variation)) {
        return this.getVariationQuantity(variation);
      }
    }
    return 0;
  }

  public isAddButtonDisabled(product: RestaurantProduct): boolean {
    return this.cartProduct == null || this.cartProduct.item.variation.disabled || this.cartProduct.item.variation.soldOut || (this.isUseInventory && (!this.isSelectedProductInStock(product) || this.currentQuantityRemaining(product) < 0));
  }

  public isApplyVariationInStock(variation: any): boolean {
    return variation && (variation.disabled || variation.soldOut || !this.isVariationInStock(variation) && variation.trackInventory && variation.useInventory);
  }

  public isMinOrMaxSet(minOrMax: number) {
    return minOrMax !== -1 && minOrMax !== null;
  }

  public formatModifierGroupRequiredStatus(productModifierGroup: ProductModifierGroup): string {
    if (!this.isSelectOneModifier(productModifierGroup)) {
      if (!this.isMinOrMaxSet(productModifierGroup.minSelectedModifiers) && !this.isMinOrMaxSet(productModifierGroup.maxSelectedModifiers)) {
        return 'Optional';
      } else if (!this.isMinOrMaxSet(productModifierGroup.minSelectedModifiers) && this.isMinOrMaxSet(productModifierGroup.maxSelectedModifiers)) {
        return `Choose ${productModifierGroup.maxSelectedModifiers} or fewer`;
      } else if (this.isMinOrMaxSet(productModifierGroup.minSelectedModifiers) && !this.isMinOrMaxSet(productModifierGroup.maxSelectedModifiers)) {
        return `Choose ${productModifierGroup.minSelectedModifiers} or more`;
      } else if (productModifierGroup.minSelectedModifiers === productModifierGroup.maxSelectedModifiers) {
        return `Choose exactly ${productModifierGroup.minSelectedModifiers}`;
      } else if (productModifierGroup.minSelectedModifiers !== productModifierGroup.maxSelectedModifiers) {
        return `Choose ${productModifierGroup.minSelectedModifiers} to ${productModifierGroup.maxSelectedModifiers}`;
      }
    }
    return this.isSelectOneModifier(productModifierGroup) ? 'Required' : 'Optional';
  }

  public isSelectOneModifier(productModifierGroup: ProductModifierGroup): boolean {
    return productModifierGroup.selectionType === ModifierSelectionType.single;
  }

  public chooseVariation(variation: ProductVariation): void {
    this.cartProduct.item.modifiers = [];
    const index = this.product.variations.indexOf(variation);
    this.cartProduct.item.variation = this.product.variations[index];
    if (variation.trackInventory && variation.useInventory) {
      let inventoryQuantity = this.cartProduct.item.quantity;
      const newVariationQuantity = inventoryQuantity + this.getVariationQuantityById(this.cartProduct.item.variation.id);
      if (inventoryQuantity > newVariationQuantity) {
        inventoryQuantity = newVariationQuantity;
        this.variationsInvalidMessage = `(only ${newVariationQuantity} remaining)`;
      } else if (inventoryQuantity <= newVariationQuantity) {
        this.variationsInvalidMessage = '';
      }
      this.cartProduct.item.quantity = inventoryQuantity === 0 ? 1 : inventoryQuantity;
    } else {
      this.variationsInvalidMessage = '';
    }
    this.cartProduct.item.calculateTotalPrice();
    this.cartProduct.item.id = this.product.variations[index].id;
    this.chosenVariation = this.product.variations[index];
  }

  public chooseModifier(event, modifiersListIndex, modifierIndex): void {
    if (this.cartProduct == null) {
      return;
    }
    const isChecked = event.target.checked;
    const chosenModifier = this.product.modifierGroups[modifiersListIndex].modifiers[modifierIndex];
    const modifiers = this.cartProduct.item.modifiers;
    this.cartProduct.item.modifiers = isChecked
      ? [...modifiers, this.deepCopyModifiers(chosenModifier)]
      : this.removeModifierFromList(modifiers, this.deepCopyModifiers(chosenModifier));
    this.cartProduct.item.calculateTotalPrice();
    this.modifiersValidations.map((group) => {
      if (group.id === this.product.modifierGroups[modifiersListIndex].id) {
        group.isValid = this.validateItemModifiers(this.product.modifierGroups[modifiersListIndex]);
      }
    });
  }

  public modifierSliderChoose(variant, modifiersListIndex, modifierIndex) {
    if (this.cartProduct == null) {
      return;
    }
    const chosenModifier = this.product.modifierGroups[modifiersListIndex].modifiers[modifierIndex];
    const modifierInCart = this.cartProduct.item.modifiers.find((modifier) => modifier.id === chosenModifier.id);
    switch (variant) {
      case 1:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Light}').trim()
          .replace('{Regular}', ' {Light}').trim()
          .replace('{Extra}', ' {Light}');
        modifierInCart.price.amount = Math.floor(chosenModifier.price.amount / 2);
        break;
      case 2:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Regular}').trim()
          .replace('{Light}', ' {Regular}').trim()
          .replace('{Extra}', ' {Regular}');
        modifierInCart.price.amount = chosenModifier.price.amount;
        break;
      case 3:
        modifierInCart.name = modifierInCart.name
          .replace('{s}', ' {Extra}').trim()
          .replace('{Light}', ' {Extra}').trim()
          .replace('{Regular}', ' {Extra}');
        modifierInCart.price.amount = Math.floor(chosenModifier.price.amount * 1.5);
        break;
    }
    this.cartProduct.item.calculateTotalPrice();
  }

  public isChosenModifier(modifiersListIndex, modifierIndex): boolean {
    if (this.cartProduct == null) {
      return false;
    }
    const chosenModifier = this.product.modifierGroups[modifiersListIndex]
      .modifiers[modifierIndex];
    return this.cartProduct.item.modifiers.filter(modifier => modifier.id === chosenModifier.id).length > 0;
  }

  public cleanedName(name: string): string {
    return name.replace('(*)', '').replace('(#)', '').replace('(^)', '').replace('{s}', '').trim();
  }

  public chooseAndRemoveModifiers(event, modifiersListIndex, modifierIndex): void {
    if (this.cartProduct == null) {
      return;
    }
    const currentModifierSet = this.product.modifierGroups[modifiersListIndex].modifiers;
    for (let i = 0; i < currentModifierSet.length; ++i) {
      const modifiers = this.cartProduct.item.modifiers;
      this.cartProduct.item.modifiers = i === modifierIndex
        ? [...modifiers, this.deepCopyModifiers(currentModifierSet[i])]
        : this.removeModifierFromList(modifiers, this.deepCopyModifiers(currentModifierSet[i]));
    }
    this.cartProduct.item.calculateTotalPrice();
  }

  public validateItemModifiers(modifierGroup: ProductModifierGroup): boolean {
    const chosenModifiersFromGroup = [];
    const { minSelectedModifiers, maxSelectedModifiers, modifiers, selectionType} = modifierGroup;
    if (this.cartProduct == null) {
      return false;
    }
    for (const modifier of modifiers) {
      this.cartProduct.item.modifiers.map((menuItem) => {
        if (menuItem.id === modifier.id && modifier.isShow) {
          chosenModifiersFromGroup.push(menuItem);
        }
      });
    }
    if (selectionType !== ModifierSelectionType.single && !this.formatDataService.containsKeyword(modifierGroup.name, 'HIDDEN')) {
      if (!this.isMinOrMaxSet(minSelectedModifiers) && !this.isMinOrMaxSet(maxSelectedModifiers)) {
        return true;
      } else if (!this.isMinOrMaxSet(minSelectedModifiers) && this.isMinOrMaxSet(maxSelectedModifiers)) {
        return chosenModifiersFromGroup.length <= maxSelectedModifiers;
      } else if (this.isMinOrMaxSet(minSelectedModifiers) && !this.isMinOrMaxSet(maxSelectedModifiers)) {
        return chosenModifiersFromGroup.length >= minSelectedModifiers;
      } else if (minSelectedModifiers === maxSelectedModifiers) {
        return chosenModifiersFromGroup.length === minSelectedModifiers;
      } else if (minSelectedModifiers !== maxSelectedModifiers) {
        return (chosenModifiersFromGroup.length >= minSelectedModifiers && chosenModifiersFromGroup.length <= maxSelectedModifiers);
      }
    } else {
      return true;
    }
  }

  public findModifierPrice(modifier) {
    if (this.cartProduct == null) {
      return;
    }
    const modifierInCart = this.cartProduct.item.modifiers.find((mod) => mod.id === modifier.id);
    if (modifierInCart) {
      return modifierInCart.price.formattedAmount;
    }
    return modifier.price.formattedAmount;
  }

  public findModifierSliderParam(modifier): string {
    const modifierInCart = this.cartProduct.item.modifiers.find((mod) => mod.id === modifier.id);

    if (modifierInCart) {
      if (modifierInCart.name.includes('{Light}')) {
        return 'Light';
      } else if (modifierInCart.name.includes('{Regular}')) {
        return 'Regular';
      } else if (modifierInCart.name.includes('{Extra}')) {
        return 'Extra';
      }
    }
    return 'Regular';
  }

  public getFormattedPrice(price: string | number, quantity = 1): string {
    return (+price * quantity).toFixed(2);
  }

  public getFilteredVariations(itemVariations: ProductVariation[]): ProductVariation[] {
    const filteredVariationList: ProductVariation[] = [];
    for (const variation of itemVariations) {
      variation.disabled = false;
      let variationHidden = false;
      if ((this.restaurant?.isAcceptingOrders
        && !this.validateVariationAvailableTimes(variation))) {
        variation.disabled = true;
        variationHidden = !this.restaurant.showAllTimeRestrictedItems;
      }
      if (variation.isFulfillmentRestrictionEnabled
        && !this.leadTimeService.validFulfillments(variation, this.restaurantStore.enabledFulfillments)) {
        variation.disabled = true;
        variationHidden = variationHidden || !this.restaurant.showAllFulfillmentRestrictedItems;
      }
      if (!variationHidden) {
        filteredVariationList.push(variation);
      }
    }
    return filteredVariationList;
  }

  public goToCheckoutPage(): void {
    if (this.isValid) {
      if (this.itemService.checkServiceItem()) {
        this.findServiceItemAndRemove();
      }
      this.cartStore.addItemToCart(this.cartProduct);
      const locationUrl = this.routeService.getLocationUrl();
      this.router.navigate([locationUrl, 'checkout']).catch(error => console.error(error));
      this.sortVariations();
    } else {
      this.showInvalidMessage = true;
    }
  }
  public cancel() {
    this.cartStore.deleteItemsFromCartByLocationId(this.locationId);
    this.restaurantStore.backToStorePage(true);
  }

  public isModifiersHidden(modifiers: ProductModifier[]): boolean {
    return modifiers.every(modifier => !modifier.isShow);
  }

  private validateVariationAvailableTimes(variation: ProductVariation): boolean {
    let isValidLeadTime = true;
    let isValidWeekDay = true;
    if (variation == null) {
      return false;
    }
    if (variation.isLeadTimesEnabled) {
      isValidLeadTime = this.leadTimeService.validLeadTime(variation, this.hoursOfOperation);
    }
    if (variation.isDailyRestrictionEnabled) {
      isValidWeekDay = this.leadTimeService.validDayRestriction(variation);
    }
    return isValidLeadTime && isValidWeekDay;
  }

  private sortVariationsByInStock(): void {
    this.product.variations.sort((a, b) =>
      this.getVariationQuantity(a) >= 0 && this.getVariationQuantity(b) < 0 ? -1 : 1
    );
  }

  private removeModifierFromList(modifiers, chosenModifier): ProductModifier[] {
    return modifiers.filter(modifier => {
      return modifier.id !== chosenModifier.id;
    });
  }

  private deepCopyModifiers(modifier: MenuItem): MenuItem {
    return new MenuItem(modifier.name, modifier.id, new Price(modifier.price.amount, modifier.price.currency));
  }
  private findServiceItemAndRemove(): void {
    this.cartStore.cartItems.forEach((item, index) => {
      if (item.isService) {
        this.cartStore.cartItems.splice(index, -1);
      }
    });
  }
}
